import { Component, computed, input, output, signal } from '@angular/core';
import { ConfirmationRequiredPayload } from 'app/shared/models/agent.model';
import { FormaButton, FormaButtonGroup } from 'forma';

/** A labelled value of the proposal shown in the card, e.g. the title of the activity to create. */
export interface ConfirmationDetail {
  label: string;
  value: string | null;
}

/**
 * Inline approval card shown when the agent proposes a tool action. Replaces
 * the native confirm/prompt with an accessible Forma card. Rejecting reveals a
 * feedback field where the user describes what to change before it's sent back
 * to the agent, which regenerates the proposal.
 */
@Component({
  selector: 'app-agent-confirmation',
  imports: [FormaButtonGroup, FormaButton],
  host: { class: 'agent-confirmation' },
  templateUrl: './agent-confirmation-card.html',
  styleUrls: ['./agent-confirmation-card.scss'],
})
export class AgentConfirmationCard {
  readonly confirmation = input.required<ConfirmationRequiredPayload>();
  readonly approveLabel = input<string>('Approve');
  readonly rejectLabel = input<string>('Reject');
  readonly cancelLabel = input<string>('Cancel');
  readonly feedbackLabel = input<string>('What would you like to change?');
  readonly saveLabel = input<string>('Save changes');
  /** What will actually be saved, so the user approves that and not only the agent's summary. Empty values are left out. */
  readonly details = input<ConfirmationDetail[]>([]);
  protected readonly shownDetails = computed(() => this.details().filter((detail) => !!detail.value?.trim()));

  readonly approve = output<void>();
  readonly reject = output<string>();

  protected readonly feedbackId = `agent-reject-${Math.random().toString(36).slice(2)}`;
  protected readonly rejecting = signal(false);
  protected readonly feedback = signal('');

  protected readonly prettyPayload = computed(() => {
    const raw = this.confirmation().proposedPayloadJson;
    if (!raw) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  });

  protected onFeedback(event: Event): void {
    this.feedback.set((event.target as HTMLTextAreaElement).value);
  }

  protected cancelReject(): void {
    this.rejecting.set(false);
    this.feedback.set('');
  }

  protected confirmReject(): void {
    this.reject.emit(this.feedback().trim());
    this.rejecting.set(false);
    this.feedback.set('');
  }
}
